import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Chip, Typography } from '@mui/material'

import { dominantMotivationsOf } from '../../helpers/creativeMotivations'
import { useCreativeTagDetails } from '../../hooks/useCreativeTagDetails'
import { useUAInsightsNavigation } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { CreativeAdsTable } from '../CreativeAdsTable/CreativeAdsTable'
import { SectionCard } from '../SectionCard/SectionCard'
import { TableSkeleton } from '../TableSkeleton/TableSkeleton'
import { UAInsightsDialog } from '../UAInsightsDialog/UAInsightsDialog'

export const CreativeTagDetailsDialog: FC = () => {
  const { t } = useTranslation('ua-insights')
  const { params, close, canGoBack, goBack, openMotivation } = useUAInsightsNavigation()
  const { tagLabel, dimensionLabel, motivationLabel } = useUATaxonomy()
  const open = params.dialog === 'tag'
  const tagId = open ? params.tagId : null
  const { data, isLoading, isError, refetch } = useCreativeTagDetails(tagId)
  const motivations = useMemo(() => dominantMotivationsOf(data?.creatives ?? []), [data?.creatives])

  const title = data ? `${dimensionLabel(data.tag.tag.dimensionId)}: ${tagLabel(data.tag.tag)}` : t('tag_details')

  return (
    <UAInsightsDialog
      open={open}
      onClose={close}
      title={title}
      canGoBack={canGoBack}
      onGoBack={goBack}
      showNetworkSelector
      hasData={!!data}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      loadingPlaceholder={<TableSkeleton rows={10} columns={6} />}
      notFoundFallback={
        <Typography variant="body2" color="text.secondary">
          {t('tag_details_not_found')}
        </Typography>
      }
    >
      {data && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <SectionCard title={t('associated_motivations')}>
            {motivations.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {motivations.map((m) => (
                  <Chip
                    key={m.id}
                    label={motivationLabel(m)}
                    size="small"
                    clickable
                    onClick={() => openMotivation(m.id)}
                    sx={{ '&:hover': { backgroundColor: 'primary.main', color: 'primary.contrastText' } }}
                  />
                ))}
              </Box>
            )}
          </SectionCard>

          <CreativeAdsTable rows={data.creatives} />
        </Box>
      )}
    </UAInsightsDialog>
  )
}
